/*
 * tt-infinite-scroll.js
 * ---------------------------------------------------------------
 * Replaces Dawn's pagination on collection + search pages with
 * scroll-triggered fetching of the next page. The new <li> items
 * are appended to the existing #product-grid <ul>.
 *
 * Behaviour:
 *   - Only activates if a "next" link is found in .pagination__list.
 *   - Hides Dawn's pagination nav.
 *   - Uses IntersectionObserver on a sentinel ~800px below the grid.
 *   - Reattaches automatically when Dawn's facet form replaces the
 *     grid after a filter/sort change.
 */
(function () {
  var loading = false;
  var paused = false; /* suspended while a collection-search filter is active */
  var nextUrl = null;
  var list = null;
  var observer = null;
  var sentinel = null;
  var epoch = 0;        /* bumps when the grid is replaced (facet/sort) so an
                           in-flight page fetch from the OLD grid state can't
                           append stale items into the new one */
  var retryIdx = 0;     /* consecutive page-fetch failures */
  var retryAt = 0;      /* no new attempts before this timestamp (backoff) */
  var statusEl = null;

  function pageFromUrl(href) {
    try { return parseInt(new URL(href, location.href).searchParams.get('page')) || 1; }
    catch (e) { return 1; }
  }

  function findNextUrl(scope, currentPage) {
    var paglist = scope.querySelector('.pagination__list');
    if (!paglist) return null;
    var anchors = paglist.querySelectorAll('a[href]');
    for (var i = 0; i < anchors.length; i++) {
      if (pageFromUrl(anchors[i].href) === currentPage + 1) return anchors[i].href;
    }
    if (anchors.length) {
      var last = anchors[anchors.length - 1];
      if (pageFromUrl(last.href) > currentPage) return last.href;
    }
    return null;
  }

  function getList(grid) {
    if (!grid) return null;
    if (grid.tagName === 'UL') return grid;
    return grid.querySelector('ul.product-grid, ul.grid, ul');
  }

  function ensureSentinel(parent) {
    if (sentinel && sentinel.isConnected) return sentinel;
    sentinel = document.createElement('div');
    sentinel.className = 'tt-infinite-sentinel';
    // display:block is required — Dawn's base.css hides empty divs
    // (div:empty { display:none }), which stops the IntersectionObserver
    // from ever firing on this sentinel.
    sentinel.style.cssText = 'display:block;height:1px;margin-top:60px;';
    parent.appendChild(sentinel);
    return sentinel;
  }

  function ensureStatus(parent) {
    if (statusEl && statusEl.isConnected) return statusEl;
    statusEl = document.createElement('div');
    statusEl.className = 'tt-infinite-status';
    statusEl.textContent = 'Keep scrolling to view more ↓';
    parent.appendChild(statusEl);
    return statusEl;
  }

  function setStatus(state) {
    if (!statusEl) return;
    statusEl.classList.remove('is-loading', 'is-done');
    if (state === 'loading') {
      statusEl.textContent = 'Loading more…';
      statusEl.classList.add('is-loading');
    } else if (state === 'done') {
      statusEl.textContent = "You've reached the end";
      statusEl.classList.add('is-done');
    } else {
      statusEl.textContent = 'Keep scrolling to view more ↓';
    }
  }

  function loadNext() {
    if (paused || loading || !nextUrl || !list) return;
    if (Date.now() < retryAt) return; /* backing off after failures */
    var myEpoch = epoch;
    loading = true;
    setStatus('loading');
    var loadedUrl = nextUrl;
    var loadedPage = pageFromUrl(loadedUrl);
    fetch(loadedUrl, { headers: { 'X-Requested-With': 'fetch' } })
      .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.text(); })
      .then(function (html) {
        // A search filter may have switched on mid-fetch — discard this page
        // without appending or advancing; resume() will re-request it later.
        if (paused) { loading = false; return; }
        // A facet/sort re-render swapped the grid mid-fetch: this response is
        // for the OLD filter state. Appending it would mix unfiltered items
        // into the filtered grid and clobber nextUrl with the old chain.
        if (myEpoch !== epoch) { loading = false; return; }
        retryIdx = 0; retryAt = 0;
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var newGrid = tmp.querySelector('#product-grid');
        var newList = getList(newGrid);
        if (newList && list.isConnected) {
          var children = Array.prototype.slice.call(newList.children);
          for (var i = 0; i < children.length; i++) list.appendChild(children[i]);
        }
        nextUrl = findNextUrl(tmp, loadedPage);
        if (!nextUrl) {
          if (observer) observer.disconnect();
          if (sentinel && sentinel.parentNode) sentinel.parentNode.removeChild(sentinel);
          setStatus('done');
        } else {
          setStatus('more');
        }
        loading = false;
        // Keep going if the sentinel is still in view (short pages / wide grids).
        if (nextUrl) requestAnimationFrame(maybeLoad);
      })
      .catch(function () {
        setStatus('more');
        loading = false;
        /* Back off before the scroll listener can re-request a failing URL —
           an immediate retry hits the same drained rate-limit bucket. */
        var delays = [2000, 5000, 10000];
        retryAt = Date.now() + delays[Math.min(retryIdx, delays.length - 1)];
        retryIdx++;
      });
  }

  // True when the sentinel is within the 800px preload zone of the viewport.
  function sentinelInView() {
    if (!sentinel) return false;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return sentinel.getBoundingClientRect().top <= vh + 800;
  }

  // Loads the next page if the sentinel is (still) in view. Needed because an
  // IntersectionObserver won't re-fire while the sentinel stays continuously
  // visible (e.g. short pages at 8/10 columns) — so we re-check after each load
  // and on scroll/resize.
  function maybeLoad() {
    if (!paused && !loading && nextUrl && sentinelInView()) loadNext();
  }

  // Suspend / resume appending without losing our place in the collection.
  // Called by tt-collection-search.js when its Enter-to-filter view takes
  // over the grid; nextUrl + list are preserved so resume() picks up exactly
  // where it left off once the filter is cleared.
  function pause() {
    paused = true;
    if (observer) observer.disconnect();
    if (sentinel) sentinel.style.display = 'none';
    if (statusEl) statusEl.style.display = 'none';
  }
  function resume() {
    paused = false;
    if (statusEl) statusEl.style.display = '';
    if (sentinel) {
      sentinel.style.display = 'block';
      if (observer) observer.observe(sentinel);
    }
    maybeLoad();
  }
  window.ttInfiniteScroll = { pause: pause, resume: resume };

  function init() {
    var grid = document.getElementById('product-grid');
    if (!grid) return;
    list = getList(grid);
    if (!list) return;

    var currentPage = pageFromUrl(location.href);
    nextUrl = findNextUrl(document, currentPage);
    if (!nextUrl) return;

    var pagination = document.querySelector('nav.pagination, .pagination-wrapper');
    if (pagination) pagination.style.display = 'none';

    var sentinelHost = grid.parentNode;
    ensureStatus(sentinelHost);
    ensureSentinel(sentinelHost);
    setStatus('more');

    if (observer) observer.disconnect();
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) loadNext(); });
    }, { rootMargin: '800px' });
    observer.observe(sentinel);
  }

  // Re-init when Dawn replaces #product-grid (after facet filter/sort).
  function watchForGridReplacement() {
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.id === 'product-grid' || (node.querySelector && node.querySelector('#product-grid'))) {
            // Grid was replaced — reset state and re-init.
            epoch++; /* invalidate any in-flight page fetch from the old state */
            // A fresh server-rendered grid (facet/sort applied) must never
            // inherit a paused state left over from an in-collection search,
            // or scrolling stays dead on the filtered results.
            paused = false;
            nextUrl = null;
            list = null;
            if (sentinel && sentinel.parentNode) sentinel.parentNode.removeChild(sentinel);
            sentinel = null;
            if (statusEl && statusEl.parentNode) statusEl.parentNode.removeChild(statusEl);
            statusEl = null;
            init();
            return;
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      watchForGridReplacement();
    });
  } else {
    init();
    watchForGridReplacement();
  }
  // Fallback triggers so a load can never get wedged when the sentinel stays
  // in view (observer won't re-fire) — re-check on scroll and on layout changes
  // such as the density column toggle.
  window.addEventListener('scroll', maybeLoad, { passive: true });
  window.addEventListener('resize', maybeLoad, { passive: true });
})();
